import { useMutation } from "@apollo/client";
import { CloudFramePrimaryButton } from "@cloudframe/button";
import { CloudFrameCheckbox } from "@cloudframe/checkbox";
import { CloudFrameDatepicker } from "@cloudframe/datepicker";
import { CloudFrameDropdown } from "@cloudframe/dropdown";
import { CloudFrameTextBox } from "@cloudframe/textbox";
import {
  DayOfWeek,
  IDropdownProps,
  ITextFieldProps,
  defaultDatePickerStrings,
} from "@fluentui/react";
import { FormEvent, ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../@styles/css/form-element.css";
import { IForm } from "../../@types/form.types";
import {
  isCarryoverEnabledProduct,
  ENVIRONMENT,
  LICENSE_STATUS,
  LICENSE_TYPE,
  PRODUCT,
} from "../../constants/license.constants";
import {
  MUTATION_CREATE_LICENSE,
  MUTATION_EDIT_LICENSE,
} from "../../graphql-mutations/license.mutation";
import { getStartingOfDay, getTomorrow } from "../../utils/date.utils";
import { parseInt } from "../../utils/numeric.utils";
import { isBlank } from "../../utils/string.utils";
import CustomLabel from "../custom-label/custom-label";
import { useStyles } from "./license-form.styles";

const LicenseForm = (props: IForm) => {
  const { data } = props;
  const classes = useStyles();
  const [formValues, setFormValues] = useState<any>({
    isOfflineLicense: false,
  });
  const [errors, setErrors] = useState<any>({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [createLicense] = useMutation(MUTATION_CREATE_LICENSE);
  const [editLicense] = useMutation(MUTATION_EDIT_LICENSE);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
  const jiraTicketPattern = /^https:\/\/cloudframe\.atlassian\.net\/.+$/;

  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      checkLicenseExpiration(data);
    }
    if (data?.id) {
      setFormValues({
        ...data,
        linesOfCodeCategory:
          data.linesOfCodeCategory === 10000 ? "HIGH" : "LOW",
      });
    } else {
      setFormValues({
        licenseType: "",
        product: "",
        linesOfCode: 0,
        SmartDocLOC: 0,
        AtlasLoc: 0,
        platformEffectiveDate: new Date(),
        platformExpiryDate: undefined,
        consultingHours: 1,
        enableCDN: "true",
        isOfflineLicense: true,
        linesOfCodeCategory: "LOW",
        supportLevel: "PREMIUM",
        productTrainingProvided: true,
        environment: "",
        serviceDeskID:"",
        CarryOverLOC: false,
        isSmartDoc: false,
      });
    }
  }, [data]);

  const validateForm = () => {
    const newErrors: any = {};
    const isBaseDcioLicense =
      (formValues.licenseType === LICENSE_TYPE.Customer ||
        formValues.licenseType === LICENSE_TYPE.Pilot) &&
      formValues.product === PRODUCT.Base_Runtime;
    const normalizedEffectiveDate = formValues.platformEffectiveDate
      ? getStartingOfDay(new Date(formValues.platformEffectiveDate))
      : undefined;
    if (isBlank(formValues.licenseType)) {
      newErrors.licenseType = "Please select an option";
    }
    if (isBlank(formValues.product)) {
      newErrors.product = "Please select an option";
    }
    if (isBlank(formValues.environment)) {
      newErrors.environment = "Please select an option";
    }
    if (isBlank(formValues.linesOfCodeCategory)) {
      newErrors.linesOfCodeCategory = "Please select an option";
    }
    if (isBlank(formValues.platformEffectiveDate)) {
      newErrors.platformEffectiveDate = "Please select a date";
    } else {
      if (
        normalizedEffectiveDate &&
        normalizedEffectiveDate < getStartingOfDay(new Date())
      ) {
        newErrors.platformEffectiveDate = "Effective date must be today or onward";
      }
    }
    if (!isBaseDcioLicense && isBlank(formValues.platformExpiryDate)) {
      newErrors.platformExpiryDate = "Please select a date";
    } else if (formValues.platformExpiryDate) {
      const normalizedExpiryDate = getStartingOfDay(
        new Date(formValues.platformExpiryDate)
      );
      if (
        normalizedEffectiveDate &&
        normalizedExpiryDate <= normalizedEffectiveDate
      ) {
        newErrors.platformExpiryDate =
          "Expiry date must be greater than effective date";
      }
    }
    if(isBlank(formValues.serviceDeskID)){
      newErrors.serviceDeskID = "Please enter the value"
    }
    if (isBlank(formValues.supportLevel)) {
      newErrors.supportLevel = "Please select an option";
    }
    if (isBlank(formValues.productTrainingProvided)) {
      newErrors.productTrainingProvided = "Please select an option";
    }
    console.log(formValues.consultingHours);
    if (isBlank(formValues.consultingHours)) {
      newErrors.consultingHours = "Please provide a valid number";
    }
    if (!formValues.email || !emailPattern.test(formValues.email)) {
      newErrors.email = "Pleae enter valid email address";
    }
    if (
      !formValues.jiraTicket ||
      !jiraTicketPattern.test(formValues.jiraTicket)
    ) {
      newErrors.jiraTicket = "Pleae enter valid Jira ticket URL";
    }

    if (formValues.licenseType == LICENSE_TYPE.Training) {
      if (isBlank(formValues.allowedProgram)) {
        newErrors.allowedProgram = "Please provide allowed program hash names";
      }
      if (isBlank(formValues.allowedJcl)) {
        newErrors.allowedJcl = "Please provide allowed JCL hash names";
      }
    }

    setErrors({ ...newErrors });
    return !Object.keys(newErrors).length;
  };

  const updateLicenseDetails = async () => {
    const {
      product,
      customerName,
      email,
      __typename,
      isOfflineLicense,
      ...licenseDataToUpdate
    } = formValues;

    const dataToUpdate = {
      ...licenseDataToUpdate,
      CarryOverLOC: isCarryoverEnabledProduct(formValues.product)
        ? !!formValues.CarryOverLOC
        : false,
    };

    const result = await editLicense({
      variables: {
        data: dataToUpdate,
      },
    });
    if (result.data.editLicense) {
      navigate("/all-licenses");
    }
  };

  const onFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormSubmitAttempted(true);
     const isFormValid = validateForm();
   // const isFormValid = true;
    try {
      if (isFormValid && data) {
        await updateLicenseDetails();
      } else if (isFormValid) {
        const loc =
          formValues.product === "Relocate"
            ? Number.MAX_SAFE_INTEGER
            : formValues.linesOfCode;
        const isBaseDcioLicense =
          (formValues.licenseType === LICENSE_TYPE.Customer ||
            formValues.licenseType === LICENSE_TYPE.Pilot) &&
          formValues.product === PRODUCT.Base_Runtime;
        const formattedExpiryDate =
          !isBaseDcioLicense && formValues.platformExpiryDate
            ? new Date(formValues.platformExpiryDate).toISOString()
            : undefined;
        const formData = {
          ...formValues,
          enableCDN: formValues.enableCDN === "true",
          CarryOverLOC: isCarryoverEnabledProduct(formValues.product)
            ? !!formValues.CarryOverLOC
            : false,
          linesOfCode: loc,
          platformEffectiveDate: formValues.platformEffectiveDate
            ? new Date(formValues.platformEffectiveDate).toISOString()
            : undefined,
          ...(formattedExpiryDate
            ? { platformExpiryDate: formattedExpiryDate }
            : {}),
        };
        console.log('Creating the license with the form data ', formData);
        const result = await createLicense({ variables: { data: formData } });
        console.log("License created");
        if (result.data.createLicense) {
          navigate("/all-licenses");
        }
      }
    } catch (error) {
      console.log(error);
    }
    setFormSubmitting(false);
  };

  const onRenderLabel = (
    props: ITextFieldProps | IDropdownProps | undefined
  ): ReactElement =>
    props ? (
      <CustomLabel id={props.label?.replaceAll(" ", "")} {...props} />
    ) : (
      <div />
    );

  const renderSmartDocSupportHint = () => {
    return (
      <div
        style={{
          fontSize: "12px",
          color: "#eb7734", // subtle grey
          marginTop: "4px",
        }}
      >
        SmartDoc is supported for CN versions 4.0.0 and below.
      </div>
    );
  };

  const onLocCategoryNav = () => {
    // eslint-disable-next-line no-restricted-globals
    open(
      "https://internal.cloudframe.com/wp/2021/10/26/generating-licenses-for-customers/",
      "_blank",
      ""
    );
  };

  const getLocCategoryHint = (): ReactElement => {
    return (
      <span>
        Categories are five-digit numbers with each digit correlating to
        different usages. Currently, CloudFrame had allocated only the first
        digit and intends to use the remaining four digits in the future for
        different usage. The below section of the article explains the usage of
        the first digit of the category which is correlating to copybooks. For
        more details, please follow
        <span
          role="link"
          onKeyDown={onLocCategoryNav}
          onClick={onLocCategoryNav}
          style={{ color: "blue", cursor: "pointer" }}
          tabIndex={0}
        >
          {" these instructions."}
        </span>
      </span>
    );
  };

  const getCarryoverHint = (): ReactElement => {
    return(
        <span>
        Carryover support was introduced in version 2.x.x and above. To create licenses for versions earlier than this, please select ‘No’.
        <span
          role="link"
          onKeyDown={onLocCategoryNav}
          onClick={onLocCategoryNav}
          style={{ color: "blue", cursor: "pointer" }}
          tabIndex={0}
        >
        </span>
      </span>
      
    )
  }

  const isLicenseExpired = (licenseInfo: any): boolean => {
    const effectiveDate = new Date(licenseInfo.platformEffectiveDate);
    const expiryDate = new Date(licenseInfo.platformExpiryDate);
    const currentDate = new Date();
    return effectiveDate <= currentDate && expiryDate < currentDate;
  };

  const checkLicenseExpiration = (licenseInfo: any): void => {
    if (isLicenseExpired(licenseInfo)) {
      licenseInfo.status = LICENSE_STATUS.EXPIRED;
    }
  };

  const onChangeLicenseType = (e: any, item: any) => {
    if ([LICENSE_TYPE.Training].includes(item.key)) {
      setFormValues({
        ...formValues,
        licenseType: item.key,
        linesOfCode: Number.MAX_SAFE_INTEGER,
        consultingHours: 0,
      });
    } else {
      setFormValues({
        ...formValues,
        licenseType: item.key,
      });
    }
    setErrors({ ...errors, licenseType: undefined });
  };

  const onChangeEnvironmentType = (event, option) => {
    setFormValues({
      ...formValues,
      environment: option.key
    });
  }

  const onNumberFieldWheel = (e: any) => {
    // Prevent browser number input wheel-step from changing values unexpectedly.
    e.currentTarget.blur();
  };

  return (
    <div className={classes.root}>
      <div
        id="form"
        className={classes.formBody}
        style={{ borderRadius: "25px" }}
      >
        <div className={classes.subContainer}>
          <h3>{data ? "Renew License" : "Create new license"}</h3>
          <form onSubmit={onFormSubmit}>
            <CloudFrameDropdown
              placeholder="Select an option"
              label="License Type"
              className={classes.mb8}
              errorMessage={errors.licenseType}
              required
              defaultSelectedKey={[formValues.licenseType]}
              onChange={onChangeLicenseType}
              options={[
                { key: LICENSE_TYPE.POT, text: LICENSE_TYPE.POT },
                { key: LICENSE_TYPE.POC, text: LICENSE_TYPE.POC },
                { key: LICENSE_TYPE.Customer, text: LICENSE_TYPE.Customer },
                { key: LICENSE_TYPE.Training, text: LICENSE_TYPE.Training },
                { key: LICENSE_TYPE.Pilot, text: LICENSE_TYPE.Pilot },
                { key: LICENSE_TYPE.Partner, text: LICENSE_TYPE.Partner },
                { key: LICENSE_TYPE.Internal, text: LICENSE_TYPE.Internal },
                { key: LICENSE_TYPE.POC_ONPREM, text: LICENSE_TYPE.POC_ONPREM}
              ]}
            />
            <CloudFrameTextBox
              onChange={(_, value) => {
                setFormValues({ ...formValues, customerName: value });
              }}
              required
              label="Customer name"
              className={classes.mb8}
              value={formValues.customerName}
              disabled={!!data}
            />
            <CloudFrameTextBox
              label="Email"
              className={classes.mb8}
              value={formValues.email}
              required
              disabled={!!data}
              errorMessage={errors.email}
              onChange={(_, value) => {
                setFormValues({ ...formValues, email: value });
                if (!value || !emailPattern.test(value)) {
                  setErrors({
                    ...errors,
                    email: "Please enter valid email address",
                  });
                } else {
                  setErrors({ ...errors, email: undefined });
                }
              }}
            />
            {[LICENSE_TYPE.Training].includes(
              formValues.licenseType
            ) && (
                <CloudFrameTextBox
                  label="Allowed programs (Program hashes)"
                  ariaLabel="Please enter comma separated program hashes here. Program hashes are calculated from program file content. The license will only allow the given programs to migrate."
                  className={classes.mb8}
                  value={formValues.allowedProgram}
                  errorMessage={errors.allowedProgram}
                  onRenderLabel={onRenderLabel}
                  onChange={(_, value) => {
                    setFormValues({ ...formValues, allowedProgram: value });
                    if (!value) {
                      setErrors({
                        ...errors,
                        allowedProgram: "Please enter program hashes",
                      });
                    } else {
                      setErrors({ ...errors, allowedProgram: undefined });
                    }
                  }}
                  required
                />
              )}
            {formValues.licenseType == LICENSE_TYPE.Training && (
              <CloudFrameTextBox
                label="Allowed JCLS (JCL hashes)"
                ariaLabel="Please enter comma separated JCL hashes here. Program hashes are calculated from JCL file content. The license will only allow the given JCLs to migrate."
                className={classes.mb8}
                value={formValues.allowedJcl}
                errorMessage={errors.allowedJcl}
                onRenderLabel={onRenderLabel}
                onChange={(_, value) => {
                  setFormValues({ ...formValues, allowedJcl: value });
                  if (!value) {
                    setErrors({
                      ...errors,
                      allowedJcl: "Please enter JCL hashes",
                    });
                  } else {
                    setErrors({ ...errors, allowedJcl: undefined });
                  }
                }}
              />
            )}
            <CloudFrameDropdown
              placeholder="Select an option"
              label="Product"
              required
              errorMessage={errors.product}
              defaultSelectedKey={formValues.product}
              onChange={(e, item: any) => {
                const isRelocate = formValues.product === "Relocate";
                const isBaseRuntime = formValues.product === "Base Runtime (DCIO - Base)"
                if (isRelocate) {
                  setFormValues({
                    ...formValues,
                    product: item.key,
                    linesOfCode: Number.MAX_SAFE_INTEGER,
                  });
                  setErrors({ ...errors, product: undefined });
                } else if (isBaseRuntime) {
                  setFormValues({ ...formValues, product: PRODUCT.Base_Runtime })
                }
                else {
                  setFormValues({ ...formValues, product: item.key });
                  setErrors({ ...errors, product: undefined });
                }
              }}
              className={classes.mb8}
              options={[
                { key: PRODUCT.SmartDoc, text: PRODUCT.SmartDoc },
                { key: PRODUCT.Transform, text: PRODUCT.Transform },
                { key: PRODUCT.Base_Runtime, text: "Base Runtime (DCIO - Base)" },
                { key: PRODUCT.Runtime_Pro, text: "Runtime Pro (DCIO - Plus)" },
                {
                  key: PRODUCT.Relocate, text: PRODUCT.Relocate
                },
                {key: PRODUCT.Atlas, text: PRODUCT.Atlas}
              ]}
            />
            {(formValues.product == PRODUCT.Atlas || formValues.product == PRODUCT.SmartDoc || formValues.product == PRODUCT.Transform) && (
              <CloudFrameDropdown
                placeholder="Select an option"
                label="Environment"
                className={classes.mb8}
                errorMessage={errors.environment}
                required
                defaultSelectedKey={[formValues.environment]}
                onChange={onChangeEnvironmentType}
                options={[
                  { key: ENVIRONMENT.ONPREM, text: ENVIRONMENT.ONPREM },
                  { key: ENVIRONMENT.SAAS, text: ENVIRONMENT.SAAS }
                ]}
              />)}
            {(formValues.product == PRODUCT.Relocate) && (
              <CloudFrameDropdown
                placeholder="Select an option"
                label="Environment"
                className={classes.mb8}
                errorMessage={errors.environment}
                required
                defaultSelectedKey={[formValues.environment]}
                onChange={onChangeEnvironmentType}
                options={[
                  { key: ENVIRONMENT.ONPREM, text: ENVIRONMENT.ONPREM },
                  { key: ENVIRONMENT.ZOS, text: ENVIRONMENT.ZOS }
                ]}
              />)}
            {((formValues.product == PRODUCT.Base_Runtime || formValues.product == PRODUCT.Runtime_Pro) && formValues.licenseType == LICENSE_TYPE.Training) && (
              <CloudFrameDropdown
                placeholder="Select an option"
                label="Environment"
                className={classes.mb8}
                errorMessage={errors.environment}
                required
                defaultSelectedKey={[formValues.environment]}
                onChange={onChangeEnvironmentType}
                options={[
                  { key: ENVIRONMENT.Training, text: ENVIRONMENT.Training },
                ]}
              />)}
            {((formValues.product == PRODUCT.Base_Runtime || formValues.product == PRODUCT.Runtime_Pro) && formValues.licenseType !== LICENSE_TYPE.Training) && (
              <CloudFrameDropdown
                placeholder="Select an option"
                label="Environment"
                className={classes.mb8}
                errorMessage={errors.environment}
                required
                defaultSelectedKey={[formValues.environment]}
                onChange={onChangeEnvironmentType}
                options={[
                  { key: ENVIRONMENT.ONPREM, text: ENVIRONMENT.ONPREM },
                ]}
              />)}
            {formValues.product !== PRODUCT.Runtime_Pro && formValues.product !== PRODUCT.Base_Runtime
              && formValues.product !== PRODUCT.Atlas  && formValues.product !== PRODUCT.SmartDoc && (
                <CloudFrameTextBox
                  onChange={(_, value = "") => {
                    setFormValues({
                      ...formValues,
                      linesOfCode: parseInt(value)  
                    });
                  }}
                  onWheel={onNumberFieldWheel}
                  label= "Lines of code (LOC)"
                  type="number"
                  required
                  
                  value={`${formValues.product === "Relocate"
                    ? Number.MAX_SAFE_INTEGER
                    : formValues.linesOfCode || ""
                    }`}
                  className={classes.mb8}
                  disabled={
                    formValues.product === "Relocate" ||
                    formValues.licenseType == LICENSE_TYPE.Training
                  }
                />
              )}
              {formValues.product === PRODUCT.Transform && (
              <>
                <CloudFrameDropdown
                  placeholder="Select LOC type"
                  label="Select LOC Type"
                  className={classes.mb8}
                  required
                  defaultSelectedKey={formValues.isSmartDoc ? "smartdoc" : "atlas"}
                  onChange={(e, item: any) => {
                    setFormValues({
                      ...formValues,
                      isSmartDoc: item.key === "smartdoc",
                      // reset the other field when switching
                      SmartDocLOC: item.key === "smartdoc" ? formValues.SmartDocLOC : 0,
                      AtlasLoc: item.key === "atlas" ? formValues.AtlasLoc : 0,
                    });
                  }}
                  options={[
                    { key: "smartdoc", text: "SmartDoc LOC" },
                    { key: "atlas", text: "Atlas LOC" },
                  ]}
                />

                {formValues.isSmartDoc ? (
                  <CloudFrameTextBox
                    onChange={(_, value = "") => {
                      setFormValues({
                        ...formValues,
                        SmartDocLOC: parseInt(value),
                      });
                    }}
                    onWheel={onNumberFieldWheel}
                    label="SmartDoc LOC"
                    type="number"
                    required
                    value={`${formValues.SmartDocLOC || ""}`}
                    onRenderDescription={renderSmartDocSupportHint}
                    className={classes.mb8}
                  />
                ) : (
                  <CloudFrameTextBox
                    onChange={(_, value = "") => {
                      setFormValues({
                        ...formValues,
                        AtlasLoc: parseInt(value),
                      });
                    }}
                    onWheel={onNumberFieldWheel}
                    label="Atlas LOC"
                    type="number"
                    required
                    value={`${formValues.AtlasLoc || ""}`}
                    className={classes.mb8}
                  />
                )}
              </>
            )}
            
            {(formValues.product === PRODUCT.Atlas)
              && (
              <CloudFrameTextBox
                  onChange={(_, value = "") => {
                    setFormValues({
                      ...formValues,
                      AtlasLoc: parseInt(value),
                    });
                  }}
                  onWheel={onNumberFieldWheel}
                  label="Atlas LOC"
                  type="number"
                  required
                  value={`${formValues.AtlasLoc || "" }`}
                  className={classes.mb8}
                />
                  )}
            
            {(formValues.product === PRODUCT.SmartDoc)
              && (
                <>
                  <CloudFrameTextBox
                    onChange={(_, value = "") => {
                      setFormValues({
                        ...formValues,
                        SmartDocLOC: parseInt(value),
                      });
                    }}
                    onWheel={onNumberFieldWheel}
                    label="SmartDoc LOC"
                    type="number"
                    required
                    value={`${formValues.SmartDocLOC || ""}`}
                    className={classes.mb8}
                    onRenderDescription={renderSmartDocSupportHint}
                  />
                </>
              )}


            {formValues.product !== PRODUCT.Runtime_Pro && formValues.product !== PRODUCT.Base_Runtime
              && (
                <CloudFrameDropdown
                  placeholder="Select an option"
                  label="Lines of code category"
                  children={getLocCategoryHint()}
                  required
                  defaultSelectedKey={[formValues.linesOfCodeCategory]}
                  errorMessage={errors.linesOfCodeCategory}
                  className={classes.mb8}
                  onRenderLabel={onRenderLabel}
                  onChange={(e, item: any) => {
                    setFormValues({ ...formValues, linesOfCodeCategory: item.key });
                    setErrors({ ...errors, linesOfCodeCategory: undefined });
                  }}
                  options={[
                    { key: "LOW", text: "Copybook included in programs" },
                    { key: "HIGH", text: "Copybook Separated from programs" },
                  ]}
                />)}
            {formValues.licenseType !== LICENSE_TYPE.Training && (
              <>
                <CloudFrameDatepicker
                  className={classes.mb8}
                  firstDayOfWeek={DayOfWeek.Monday}
                  value={
                    formValues.platformEffectiveDate
                      ? new Date(formValues.platformEffectiveDate)
                      : undefined
                  }
                  label={" Platform Effective Date"}
                  minDate={new Date()}
                  isRequired={!formValues.platformEffectiveDate}
                  disabled={formValues.licenseType == LICENSE_TYPE.Training}
                  onSelectDate={(date) => {
                    const newEffectiveDate = getStartingOfDay(date);
                    const currentExpiryDate = formValues.platformExpiryDate;
                    const isBaseDcioLicense =
                      (formValues.licenseType === LICENSE_TYPE.Customer ||
                        formValues.licenseType === LICENSE_TYPE.Pilot) &&
                      formValues.product === PRODUCT.Base_Runtime;
                    const adjustedExpiryDate =
                      isBaseDcioLicense
                        ? undefined
                        : currentExpiryDate
                        ? new Date(currentExpiryDate) <
                          new Date(newEffectiveDate || "")
                          ? getTomorrow(newEffectiveDate)
                          : new Date(currentExpiryDate)
                        : getTomorrow(newEffectiveDate);

                    setFormValues({
                      ...formValues,
                      platformEffectiveDate: newEffectiveDate,
                      platformExpiryDate: adjustedExpiryDate || undefined,
                    });
                    setErrors({ ...errors, platformEffectiveDate: undefined });
                  }}
                  placeholder="Select a date..."
                  ariaLabel="Select a date"
                  strings={defaultDatePickerStrings}
                />
                {!((formValues.licenseType === LICENSE_TYPE.Customer || formValues.licenseType === LICENSE_TYPE.Pilot) && formValues.product === PRODUCT.Base_Runtime) && (
                  <CloudFrameDatepicker
                    firstDayOfWeek={DayOfWeek.Monday}
                    label={"Platform Expiry Date"}
                    isRequired={!formValues.platformExpiryDate}
                    disabled={formValues.licenseType == LICENSE_TYPE.Training}
                    minDate={getTomorrow(formValues.platformEffectiveDate)}
                    value={
                      formValues.platformExpiryDate
                        ? new Date(formValues.platformExpiryDate)
                        : undefined
                    }
                    onSelectDate={(date) => {
                      setFormValues({
                        ...formValues,
                        platformExpiryDate: date,
                      });
                      setErrors({ ...errors, expiryDate: undefined });
                    }}
                    placeholder="Select a date..."
                    ariaLabel="Select a date"
                    className={classes.mb8}
                    strings={defaultDatePickerStrings}
                  />
                )}
              </>
            )}
            <CloudFrameTextBox
              onChange={(_, value) => {
                setFormValues({ ...formValues, serviceDeskID: value });
              }}
              required
              label="Service Desk Id"
              className={classes.mb8}
              value={formValues.serviceDeskID}
              disabled={!!data}
            />
            <CloudFrameDropdown
              placeholder="Select an option"
              className={classes.mb8}
              required

              defaultSelectedKey={[formValues.supportLevel]}
              errorMessage={errors.supportLevel}
              onChange={(e, item: any) => {
                setFormValues({ ...formValues, supportLevel: item.key });
                setErrors({ ...errors, supportLevel: undefined });
              }}
              label="Support Level"
              options={[
                { key: "PREMIUM", text: "Premium" },
                { key: "PREMIUM_PLUS", text: "Premium plus" },
              ]}
            />
            {isCarryoverEnabledProduct(formValues.product) && (
              <CloudFrameDropdown
                placeholder="Select an option"
                className={classes.mb8}
                required
                defaultSelectedKey={[
                  formValues.CarryOverLOC === undefined
                    ? ""
                    : formValues.CarryOverLOC
                      ? "yes"
                      : "no",
                ]}
                onChange={(e, item: any) => {
                  setFormValues({
                    ...formValues,
                    CarryOverLOC: item.key === "yes",
                  });
                }}
                label= "CarryOverLOC Enabled"
                children={getCarryoverHint()}
                onRenderLabel={onRenderLabel}
                options={[
                  { key: "yes", text: "Yes" },
                  { key: "no", text: "No" },
                ]}
              />
            )}
            <CloudFrameDropdown
              placeholder="Select an option"
              className={classes.mb8}
              errorMessage={errors.productTrainingProvided}
              required
              defaultSelectedKey={[
                formValues.productTrainingProvided === undefined
                  ? ""
                  : formValues.productTrainingProvided
                    ? "yes"
                    : "no",
              ]}
              onChange={(e, item: any) => {
                setFormValues({
                  ...formValues,
                  productTrainingProvided: item.key === "yes",
                });
                setErrors({ ...errors, productTrainingProvided: undefined });
              }}
              label="Product training provided?"
              options={[
                { key: "yes", text: "Yes" },
                { key: "no", text: "No" },
              ]}
            />
            <CloudFrameTextBox
              label="Consulting hours in agreement"
              className={classes.mb8}
              required
              errorMessage={errors.consultingHours}
              value={`${formValues.consultingHours || "0"}`}
              type="number"
              onChange={(_, value = "") => {
                setFormValues({
                  ...formValues,
                  consultingHours: parseInt(value),
                });
              }}
            />
            <CloudFrameDropdown
              placeholder="Select an option"
              label="Enable CDN for license validation"
              required
              errorMessage={errors.enableCDN}
              defaultSelectedKey={
                [formValues.enableCDN]
              }
              onChange={(e, item: any) => {
                setFormValues({ ...formValues, enableCDN: item.key });
                setErrors({ ...errors, enableCDN: undefined });
              }}
              className={classes.mb8}
              options={[
                { key: "true", text: "Yes" },
                { key: "false", text: "No" },
              ]}
              disabled={
                formValues.product === "Relocate" ||
                formValues.licenseType == LICENSE_TYPE.Training
              }
            />
            <CloudFrameTextBox
              label="Jira Ticket Link (URL)"
              className={classes.mb8}
              value={formValues.jiraTicket}
              disabled={!!data}
              errorMessage={errors.jiraTicket}
              onChange={(_, value) => {
                setFormValues({ ...formValues, jiraTicket: value });
                const errorMessage = !jiraTicketPattern.test(value || "")
                  ? "Please provide valid Jira ticket"
                  : undefined;
                setErrors({ ...errors, jiraTicket: errorMessage });
              }}
              required
            />

            <div style={{ marginTop: "1.5rem" }}>
              <CloudFrameCheckbox
                boxSide="end"
                label="Offline license"
                styles={{
                  label: {
                    fontWeight: 600,
                  },
                }}
                name="isOfflineLicense"
                checked={formValues.isOfflineLicense}
                onChange={(_: any, checked: any) => {
                  setFormValues({
                    ...formValues,
                    isOfflineLicense: !!checked,
                  });
                }}
                disabled={!!data}
              />
            </div>
            <div className={classes.btnContainer}>
              <CloudFramePrimaryButton
                text={data ? "Renew" : "Create"}
                type="submit"
                disabled={
                  formSubmitting ||
                  (Number.isInteger(data?.id) &&
                    data?.status !== LICENSE_STATUS.EXPIRED)
                }
                className={`${classes.mt16} ${classes.ml2}`}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LicenseForm;
